// src/utils/tencentUploader.js

/**
 * 通用上传方法：上传任意文件到腾讯云（基于预签名URL）
 * 使用后端返回的 upload_url（预签名上传地址）
 *
 * @param {Blob | string} content - 文件内容 (HTML字符串或Blob)
 * @param {string} uploadUrl - 后端返回的预签名上传URL
 * @param {string} [mimeType='application/octet-stream'] - 文件类型
 * @param {function} [onProgress] - 上传进度回调 (0~100)
 * @returns {Promise<string>} - 上传完成后返回可访问URL（不带签名参数）
 */
export async function uploadToTencentCloud(
  content,
  uploadUrl,
  mimeType = 'application/octet-stream',
  onProgress
) {
  if (!uploadUrl) {
    throw new Error('❌ 缺少上传地址 upload_url')
  }

  // 统一转成 Blob
  let body
  if (typeof content === 'string') {
    body = new Blob([content], { type: mimeType })
  } else if (content instanceof Blob || content instanceof File) {
    body = content
  } else {
    throw new Error('❌ content 必须是字符串或 Blob/File 类型')
  }

  // ----------------------------
  // 👇 使用 XMLHttpRequest 支持进度监听 + CORS PUT
  // ----------------------------
  const xhr = new XMLHttpRequest()

  const uploadPromise = new Promise((resolve, reject) => {
    xhr.open('PUT', uploadUrl, true)
    xhr.setRequestHeader('Content-Type', mimeType)

    // 监听上传进度
    if (xhr.upload && typeof onProgress === 'function') {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100)
          onProgress(percent)
        }
      }
    }

    xhr.onload = function() {
      if (xhr.status >= 200 && xhr.status < 300) {
        // 上传成功
        const finalUrl = uploadUrl.split('?')[0]
        resolve(finalUrl)
      } else {
        reject(new Error(`❌ 上传失败，状态码：${xhr.status}`))
      }
    }

    xhr.onerror = function() {
      reject(new Error('❌ 上传出错，可能是 CORS 或网络问题'))
    }

    xhr.send(body)
  })

  return uploadPromise
}
